/**
 * Strategy objects that can be linked to a record in another system and kept
 * in step with it, beside Items: which table holds each, which of its fields
 * cross, and what each system calls the object and those fields.
 *
 * Canon: docs/1-canon/01-terminology-and-glossary.md › "Field Source, Sync
 * Direction and Conflict Rule" (the rules) and "Strategic Objectives" (one
 * objective, many expressions; Priorities.ai need not hold its master data).
 * The browser (the Objectives page), `dispatch` (values going out) and `apply`
 * (values coming in) all read this file. Items keep their own registry
 * (fieldSyncRules.ts, fieldSyncOutbound.ts); a field key here is never an
 * Item field key, so a connector's saved rules are keyed `<type>.<field>`.
 *
 * Pure: no I/O, no Deno or browser globals.
 */

import { NOT_SYNCED, readStoredRule, type FieldSyncRule, type StoredFieldRules } from './fieldSyncRules.ts';

export type SyncedObjectType = 'objective' | 'goal';

export interface SyncedObjectSystem {
  /** What the other system calls the record an object of this type is linked to. */
  recordLabel: string;
  /** Our field → its field, for each field it carries. */
  fields: Record<string, string>;
}

export interface SyncedObjectDef {
  type: SyncedObjectType;
  label: string;
  table: string;
  /** Every field a person sees on the object, by column, with its name in words. */
  fields: Record<string, string>;
  systems: Record<string, SyncedObjectSystem>;
}

export const SYNCED_OBJECTS: Record<SyncedObjectType, SyncedObjectDef> = {
  objective: {
    type: 'objective',
    label: 'Objective',
    table: 'strategic_objectives',
    fields: { name: 'Name', description: 'Description', status: 'Status', time_horizon: 'Time horizon', priority: 'Priority' },
    systems: {
      linear: { recordLabel: 'Linear initiative', fields: { name: 'name', description: 'description' } },
      jira: { recordLabel: 'Jira issue', fields: { name: 'summary', description: 'description' } },
    },
  },
  // A goal is a measurable target under an objective; in Linear, an initiative
  // (a sub-initiative of the objective's, where the objective is linked too).
  goal: {
    type: 'goal',
    label: 'Goal',
    table: 'goals',
    fields: {
      name: 'Name', description: 'Description', metric_name: 'Metric', current_value: 'Current value',
      target_value: 'Target value', status: 'Status', time_horizon: 'Time horizon', priority: 'Priority',
    },
    systems: {
      linear: { recordLabel: 'Linear initiative', fields: { name: 'name', description: 'description' } },
      jira: { recordLabel: 'Jira issue', fields: { name: 'summary', description: 'description' } },
    },
  },
};

export function isSyncedObjectType(type: string | null | undefined): type is SyncedObjectType {
  return !!type && Object.prototype.hasOwnProperty.call(SYNCED_OBJECTS, type);
}

/** The key a connector's rule for one field of one object type is saved under. */
export function objectRuleKey(type: SyncedObjectType, field: string): string {
  return `${type}.${field}`;
}

/**
 * What a carried field does when the connector has no rule saved for it: the
 * words that state the object are shared, and Priorities.ai wins when both
 * sides changed (the canon default). Status words differ between systems, so
 * status does not cross until a rule maps it.
 */
const DEFAULT_OBJECT_RULE: FieldSyncRule = { owner: 'priorities_ai', direction: 'both', conflict: 'owner_wins' };

/** The rule for one field of one object type on one connector. A field the system cannot carry is not synced. */
export function objectFieldRuleFor(
  rules: StoredFieldRules | null | undefined,
  connectorType: string,
  type: SyncedObjectType,
  field: string,
): FieldSyncRule {
  const theirs = SYNCED_OBJECTS[type].systems[connectorType]?.fields[field];
  if (!theirs) return NOT_SYNCED;
  return readStoredRule(rules?.[objectRuleKey(type, field)]) ?? DEFAULT_OBJECT_RULE;
}

/** Our field for a field name the other system sends (its name, or ours). Null when it carries no such field. */
export function ourObjectField(connectorType: string, type: SyncedObjectType, theirField: string): string | null {
  const fields = SYNCED_OBJECTS[type].systems[connectorType]?.fields ?? {};
  for (const [ours, theirs] of Object.entries(fields)) {
    if (theirs === theirField || ours === theirField) return ours;
  }
  return null;
}

/** Its field for one of ours. Null when it carries no such field. */
export function theirObjectField(connectorType: string, type: SyncedObjectType, ourField: string): string | null {
  return SYNCED_OBJECTS[type].systems[connectorType]?.fields[ourField] ?? null;
}
